import { Eraser, MonitorCog } from 'lucide-react';
import { useState } from 'react';
import { historyEnabled, setHistoryEnabled, clearLocalData } from '../../app/preferences';
import type { ThemePreference } from '../../app/useTheme';
import { Button } from '../../design-system/Button';
import type { ProcessingLimits } from '../../engine/core/types';
import { formatBytes } from '../../utilities/format';

interface SettingsPageProps {
  theme: ThemePreference;
  setTheme: (theme: ThemePreference) => void;
  limits: ProcessingLimits;
}

export function SettingsPage({ theme, setTheme, limits }: SettingsPageProps): React.JSX.Element {
  const [history, setHistory] = useState(historyEnabled);
  const [cleared, setCleared] = useState(false);
  const updateHistory = (enabled: boolean): void => { setHistory(enabled); setHistoryEnabled(enabled); };
  return (
    <article className="settings-page">
      <header><p className="mono label">SETTINGS</p><h1>Local preferences</h1><p>Preferences stay in this browser. Documents are never stored here.</p></header>
      <section className="settings-section">
        <div><h2>Appearance</h2><p>Theme changes apply immediately without reloading.</p></div>
        <label>Theme<select value={theme} onChange={(event) => setTheme(event.target.value as ThemePreference)}><option value="system">System</option><option value="light">Light</option><option value="dark">Dark</option></select></label>
      </section>
      <section className="settings-section">
        <div><h2>Local history</h2><p>Optional metadata only: timestamp, format, operation, outcome, duration, and issue count. No filenames, hashes, or content.</p></div>
        <label className="toggle"><input type="checkbox" checked={history} onChange={(event) => updateHistory(event.target.checked)} /><span>Save metadata-only history</span></label>
      </section>
      <section className="settings-section">
        <div><h2>Processing readiness</h2><p>MendByte selected this profile from viewport width and logical processor count.</p></div>
        <div className="profile-readout"><MonitorCog /><strong>{limits.profile}</strong><span>{formatBytes(limits.maxFileBytes)} file · {limits.maxEntries.toLocaleString()} entries · {formatBytes(limits.maxExpandedBytes)} expanded · {limits.maxWorkbookCells.toLocaleString()} workbook cells · {limits.maxWordParagraphs.toLocaleString()} Word paragraphs · {limits.maxOdtParagraphs.toLocaleString()} ODT paragraphs · {limits.maxPresentationSlides.toLocaleString()} PowerPoint slides · {limits.maxPdfPages.toLocaleString()} PDF pages · {limits.maxPdfObjects.toLocaleString()} PDF objects</span></div>
      </section>
      <section className="settings-section settings-section--danger">
        <div><h2>Clear stored data</h2><p>Removes history and privacy preferences. Theme remains so the page does not flash.</p></div>
        <Button variant="danger" onClick={() => { clearLocalData(); setHistory(false); setCleared(true); }}><Eraser />Clear local data</Button>
      </section>
      <p className="status-message" aria-live="polite">{cleared ? 'Local MendByte history and privacy preferences were cleared.' : ''}</p>
    </article>
  );
}
